import { Dispatch } from 'redux';

import {
    ToastBody,
    Symbol,
    TokenInfoPayload,
    SpotActiveOrderCancelPayload,
    SpotConditionalOrderCancelPayload,
    ActiveOrder,
    ConditionalOrder,
    SpotOrderPayload,
    SpotOrderSettingPayload,
    GetSpotLimitOrMarketOrderPayload,
    GetSpotConditionalOrderPayload,
    SpotAllActiveOrderCancelPayload,
    SpotAllConditionalOrderCancelPayload,
} from 'src/types';

import * as requestFromServer from './spotPageCrud';
import { spotPageSlice, callTypes } from './spotPageSlice';

const { actions } = spotPageSlice;

export const createOrder = (body: SpotOrderPayload) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.createOrder(body);
        dispatch(actions.orderCreated(response));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const saveCurrentSymbol = (symbol: Symbol) => (dispatch: Dispatch) => {
    dispatch(actions.saveCurrentSymbol(symbol));
};

export const updateTvWidgetRef = (tvWidgetRef: any) => (dispatch: Dispatch) => {
    dispatch(actions.updateTvWidgetRef(tvWidgetRef));
};

export const updateCreateOrderPrice = (price: string) => (dispatch: Dispatch) => {
    dispatch(actions.updateCreateOrderPrice(price));
};

export const fetchActiveOpenOrders = (queryParams: string) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchActiveOpenOrders(queryParams);
        dispatch(actions.saveActiveOpenOrders(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchConditionalOpenOrders = (queryParams: string) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchConditionalOpenOrders(queryParams);
        dispatch(actions.saveConditionalOpenOrders(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchTokenInfo = (body: TokenInfoPayload) => async (dispatch: Dispatch) => {
    try {
        const response = await requestFromServer.fetchTokenInfo(body);
        dispatch(actions.tokenInfoFetched(response));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchAvailableAssets = () => async (dispatch: Dispatch) => {
    try {
        const response = await requestFromServer.fetchAvailableAssets();
        dispatch(actions.availableAssetsFetched(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const showNotification = (body: ToastBody) => async (dispatch: Dispatch) => {
    try {
        dispatch(actions.showNotification(body));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};
export const fetchActiveTradeOrders = (queryParams: string) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchActiveTradeOrders(queryParams);
        dispatch(actions.saveActiveTradeOrders(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchConditionalTradeOrders = (queryParams: string) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchConditionalTradeOrders(queryParams);
        dispatch(actions.saveConditionalTradeOrders(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchMyTrades = (queryParams: string) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchMyTrades(queryParams);
        dispatch(actions.saveMyTrades(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const cancelActiveOrder =
    (body: SpotActiveOrderCancelPayload) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            let response = await requestFromServer.cancelActiveOrder(body);
            response = { ...response.data, orderId: body.order_id };
            dispatch(actions.activeOrderCanceled(response));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const cancelConditionalOrder =
    (body: SpotConditionalOrderCancelPayload) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            let response = await requestFromServer.cancelConditionalOrder(body);
            response = { ...response.data, orderId: body.order_id };
            dispatch(actions.conditionalOrderCanceled(response));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };
export const updateUserTradeData =
    (responseMsg: ActiveOrder | ConditionalOrder, topic: string) => (dispatch: Dispatch) => {
        dispatch(actions.socketUserDataReceived({ data: responseMsg, topic }));
    };

export const fetchOrderSetting = () => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchOrderSetting();
        dispatch(actions.orderSettingFetched(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const setOrderSetting = (body: SpotOrderSettingPayload) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        await requestFromServer.setOrderSetting(body);
        dispatch(actions.orderSettingFetched({ isConfirm: body.is_confirm }));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const getLimitOrMarketOrderDetail =
    (body: GetSpotLimitOrMarketOrderPayload) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            const response = await requestFromServer.getLimitOrMarketOrderDetail(body);
            dispatch(actions.callEnded(callTypes.action));
            return response.data;
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const getConditionalOrderDetail =
    (body: GetSpotConditionalOrderPayload) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            const response = await requestFromServer.getConditionalOrderDetail(body);
            dispatch(actions.callEnded(callTypes.action));
            return response.data;
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };
export const cancelAllActiveOrders =
    (body: SpotAllActiveOrderCancelPayload) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            let response = await requestFromServer.cancelAllActiveOrders(body);
            response = { ...response.data, symbolId: body.symbol_ids ? body.symbol_ids : 'ALL' };
            dispatch(actions.allActiveOrderCancelled(response));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };
export const cancelAllConditionalOrders =
    (body: SpotAllConditionalOrderCancelPayload) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            let response = await requestFromServer.cancelAllConditionalOrders(body);
            response = { ...response.data, symbolId: body.symbol_id ? body.symbol_id : 'ALL' };
            dispatch(actions.allConditionalOrderCancelled(response));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };
